import React, { useState } from 'react'
import { Button, IconButton, Pill } from '../ui'

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function SessionItem({ s, selectedSessionId, onSelectSession, onDeleteSession, readOnly, showCreatedLabel }) {
  const [confirmDelete, setConfirmDelete] = useState(false)

  const date = s.scheduledAt ? formatDate(s.scheduledAt) : formatDate(s.createdAt)
  const isSelected = s.sessionId === selectedSessionId
  const isCompleted = s.status === 'completed'
  const isReady = s.status === 'ready'
  const isCancelled = s.status === 'cancelled'

  function handleDeleteClick(e) {
    e.stopPropagation()
    setConfirmDelete(true)
  }

  function handleConfirmDelete(e) {
    e.stopPropagation()
    onDeleteSession?.(s.sessionId)
    setConfirmDelete(false)
  }

  function handleCancelDelete(e) {
    e.stopPropagation()
    setConfirmDelete(false)
  }

  return (
    <div
      style={{ ...styles.item, background: isSelected ? 'var(--border)' : 'transparent' }}
      onClick={() => onSelectSession(s.sessionId)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelectSession(s.sessionId)}
    >
      <div style={styles.dateGroup}>
        <span style={styles.date}>{date}</span>
        {showCreatedLabel && <span style={styles.createdLabel}>created</span>}
      </div>
      <div style={styles.badges}>
        <Pill
          tone={isCompleted ? 'success' : isReady ? 'info' : isCancelled ? 'danger' : 'neutral'}
        >
          {isCompleted ? 'Completed' : isReady ? 'Ready' : isCancelled ? 'Cancelled' : 'Draft'}
        </Pill>
        {s.status === 'draft' && !readOnly && (
          confirmDelete ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleConfirmDelete}
                style={{ color: 'var(--danger-text)', fontWeight: 600 }}
              >
                Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelDelete}>No</Button>
            </>
          ) : (
            <IconButton ariaLabel="Delete session" onClick={handleDeleteClick}>×</IconButton>
          )
        )}
      </div>
    </div>
  )
}

function startOfDay(date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

export function SessionList({ sessions, selectedSessionId, onSelectSession, onDeleteSession, readOnly = false }) {
  if (sessions.length === 0) {
    return <p style={styles.empty}>No sessions yet</p>
  }

  const today = startOfDay(new Date())
  const unscheduled = sessions.filter((s) => !s.scheduledAt)
  const scheduled = sessions.filter((s) => s.scheduledAt && startOfDay(s.scheduledAt) >= today)
  const history = sessions.filter((s) => s.scheduledAt && startOfDay(s.scheduledAt) < today)

  const groupCount = (unscheduled.length > 0 ? 1 : 0) + (scheduled.length > 0 ? 1 : 0) + (history.length > 0 ? 1 : 0)
  const showHeaders = groupCount > 1

  const commonProps = { selectedSessionId, onSelectSession, onDeleteSession, readOnly }

  return (
    <div style={styles.list} data-testid="session-list">
      {unscheduled.length > 0 && (
        <div style={styles.group}>
          {showHeaders && <p style={styles.groupLabel}>Unscheduled</p>}
          {unscheduled.map((s) => (
            <SessionItem key={s.sessionId} s={s} {...commonProps} showCreatedLabel={showHeaders} />
          ))}
        </div>
      )}
      {scheduled.length > 0 && (
        <div style={styles.group}>
          {showHeaders && <p style={styles.groupLabel}>Scheduled</p>}
          {scheduled.map((s) => (
            <SessionItem key={s.sessionId} s={s} {...commonProps} showCreatedLabel={false} />
          ))}
        </div>
      )}
      {history.length > 0 && (
        <div style={styles.group}>
          {showHeaders && <p style={styles.groupLabel}>History</p>}
          {history.map((s) => (
            <SessionItem key={s.sessionId} s={s} {...commonProps} showCreatedLabel={false} />
          ))}
        </div>
      )}
    </div>
  )
}

const styles = {
  list: { display: 'flex', flexDirection: 'column', gap: '2px' },
  group: { display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '4px' },
  groupLabel: {
    fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    padding: '4px 12px 2px', margin: 0,
  },
  item: {
    padding: '7px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  dateGroup: { display: 'flex', alignItems: 'baseline', gap: '5px' },
  date: { fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' },
  createdLabel: { fontSize: '10px', color: 'var(--text-secondary)' },
  badges: { display: 'flex', alignItems: 'center', gap: '4px' },
  empty: { fontSize: '12px', color: 'var(--text-secondary)', padding: '8px 12px' },
}
